import { Button, Grid, Image, Input, Paper, PasswordInput, Stack, TextInput, Title } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import { forEach } from 'lodash';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

/**
 * APIs
 */
import { login } from '@/apis/auth';

/**
 * Components
 */
import ComponentTheme from '@/components/theme';

const PageAuthLogin: React.FC = () => {
    /**
     * Params
     */
    const [params] = useSearchParams();

    /**
     * Hooks
     */
    const { i18n, t } = useTranslation();

    const form = useForm({
        initialValues: {
            captcha: '',
            password: '',
            username: '',
        },
        validate: {
            captcha: isNotEmpty(t('validation.captcha')),
            password: isNotEmpty(t('validation.password')),
            username: isNotEmpty(t('validation.username')),
        },
    });

    const navigate = useNavigate();

    const captchaRef = useRef<any>(null);

    /**
     * Requests
     */
    const { mutate: mutateLogin, isPending: pendingLogin } = useMutation({
        onError: () => {
            captchaRef.current?.reset();

            form.resetField('captcha');
        },
        onSuccess: ({ data }) => {
            const url = new URL(params.get('redirect') as string);

            url.searchParams.set('access_token', data.access_token as string);

            window.location.href = url.toString();
        },
        mutationFn: login,
    });

    /**
     * Effects
     */
    useEffect(() => {
        if (captchaRef.current) {
            captchaRef.current.destroy();

            form.resetField('captcha');
        }

        forEach(form.errors, (_, key) => {
            form.validateField(key);
        });

        initGeetest4(
            {
                captchaId: '19bebfb6a61b6eb10f3ec8975a16ad52',
                language: i18n.language === 'en' ? 'eng' : 'zho',
                nativeButton: {
                    width: '100%',
                    height: '42px',
                },
            },
            (captcha) => {
                captcha.appendTo('#captcha');

                captcha.onSuccess(() => {
                    form.setFieldValue('captcha', captcha.getValidate());
                });

                captchaRef.current = captcha;
            },
        );

        return () => {
            captchaRef.current?.destroy();
        };
    }, [i18n.language]);

    useEffect(() => {
        if (params.get('redirect')) return;

        navigate('/');
    }, [params]);

    return (
        <Grid gap={0}>
            <Grid.Col span={6}>
                <Stack h="100dvh" justify="space-between" p="md">
                    <div></div>

                    <form
                        style={{ width: 400, marginInline: 'auto' }}
                        onSubmit={form.onSubmit((values) => mutateLogin(values))}
                    >
                        <Title mb={32} order={2}>
                            {t('login')}
                        </Title>

                        <Stack gap={24}>
                            <TextInput
                                label={t('username')}
                                size="md"
                                withAsterisk
                                {...form.getInputProps('username')}
                            />

                            <PasswordInput
                                label={t('password')}
                                size="md"
                                withAsterisk
                                {...form.getInputProps('password')}
                            />

                            <Input.Wrapper
                                label={t('captcha')}
                                size="md"
                                withAsterisk
                                {...form.getInputProps('captcha')}
                            >
                                <Paper h={42} id="captcha" key={i18n.language} />
                            </Input.Wrapper>

                            <Button fullWidth loading={pendingLogin} size="md" type="submit">
                                {t('login')}
                            </Button>
                        </Stack>
                    </form>

                    <ComponentTheme />
                </Stack>
            </Grid.Col>

            <Grid.Col span={6}>
                <Image h="100dvh" src="/auth/background.jpg" />
            </Grid.Col>
        </Grid>
    );
};

export default PageAuthLogin;
